/**
 * WorkspaceDashboardScreen.js
 *
 * The configurable, multi-workspace Manager dashboard - the phone counterpart of
 * the admin's global dashboard (`/main/dashboard`, "boards"):
 *
 *  - workspaces as swipeable tabs (add / rename / reorder / delete / duplicate),
 *    persisted on the ACCOUNT in the `mobileWorkspaces` namespace of
 *    `user.dashboardConfig` (merged per namespace, so admin boards and the legacy
 *    manager layout are never touched) with an offline mirror;
 *  - per-role templates (one tap -> a new workspace) and AI generation with a
 *    preview (`POST /api/dashboardWidgetAi/generate`, `target: 'mobile'`);
 *  - edit mode: add from the widget library (only widgets the backend lets this
 *    user open), remove, reorder, size S/M/L, per-widget time range and store;
 *  - widgets fed by the real `GET /api/main-dashboard/summary` (same data the
 *    admin shows); skeletons, pull-to-refresh, empty / locked / error states.
 *
 * The first workspace is the unchanged classic Manager dashboard (the migration of
 * the old layout), rendered by `ManagementDashboardScreen` with the tab strip
 * injected under its greeting header.
 *
 * @module screens/WorkspaceDashboardScreen
 */

import React, {useCallback, useMemo, useRef, useState} from 'react';
import {PanResponder, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import ManagementScreen from '../components/management/ManagementScreen';
import {EmptyState, PrimaryButton} from '../components/management/ManagementUI';
import Icon from '../components/management/SafeIcon';
import {GreetingHeader, PillTabs} from '../components/premium';
import NotificationBellButton from '../components/NotificationBellButton';
import ChatHeaderButton from '../components/chat/ChatHeaderButton';
import HappyAlert from '../components/HappyAlert';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';
import {useCurrentUser} from '../helpers/useCurrentUser';
import {useManagementData} from '../helpers/ManagementDataContext';
import {resolveDashboardProfile, useManagementAccess} from '../helpers/managementAccess';
import {MANAGEMENT_HERO_TITLE_KEY} from '../helpers/managementDashboardProfile';
import {useManagementTheme} from '../helpers/managementTheme';
import haptics from '../helpers/haptics';
import {WORKSPACE_WIDGETS} from '../config/workspaceCatalog';
import {MAX_WIDGETS, MAX_WORKSPACES, WORKSPACE_ERRORS, WORKSPACE_PERIODS, isWidgetBoard} from '../helpers/workspaces/workspaceModel';
import {widgetsFromIds} from '../helpers/workspaces/workspaceWidgets';
import {templateKeys} from '../helpers/workspaces/workspaceTemplates';
import {isWidgetAllowed, listUserStores, requestKey} from '../helpers/workspaces/workspaceData';
import {useWorkspaces} from '../helpers/workspaces/useWorkspaces';
import {useAccessibleModules, useWorkspaceData} from '../helpers/workspaces/useWorkspaceData';
import WorkspaceTabStrip from '../components/workspaces/WorkspaceTabStrip';
import WorkspaceWidget from '../components/workspaces/WorkspaceWidgets';
import WorkspaceEditFrame from '../components/workspaces/WorkspaceEditFrame';
import WorkspaceManageSheet from '../components/workspaces/WorkspaceManageSheet';
import WorkspaceCreateSheet from '../components/workspaces/WorkspaceCreateSheet';
import WorkspaceTemplatesSheet from '../components/workspaces/WorkspaceTemplatesSheet';
import WorkspaceAiSheet from '../components/workspaces/WorkspaceAiSheet';
import WorkspaceLibrarySheet from '../components/workspaces/WorkspaceLibrarySheet';
import WorkspaceWidgetSettingsSheet from '../components/workspaces/WorkspaceWidgetSettingsSheet';
import {useWorkspaceLabels} from '../components/workspaces/WorkspaceUI';
import ManagementDashboardScreen from './ManagementDashboardScreen';

/** Horizontal distance (px) that counts as a tab swipe. */
const SWIPE_DISTANCE = 70;

/**
 * Horizontal swipe -> neighbouring workspace. Claims the gesture only when it is
 * clearly horizontal, so vertical scrolling is never stolen.
 *
 * @param {() => void} onNext Swipe left.
 * @param {() => void} onPrevious Swipe right.
 * @param {boolean} enabled Off while editing.
 * @returns {object} `panHandlers` to spread on a View.
 */
const useTabSwipe = (onNext, onPrevious, enabled) => {
    const latest = useRef({onNext, onPrevious, enabled});

    latest.current = {onNext, onPrevious, enabled};

    return useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponder: (event, gesture) => latest.current.enabled
            && Math.abs(gesture.dx) > 24 && Math.abs(gesture.dx) > Math.abs(gesture.dy) * 2.2,
        onPanResponderRelease: (event, gesture) => {
            if (!latest.current.enabled) {
                return;
            }

            if (gesture.dx <= -SWIPE_DISTANCE) {
                latest.current.onNext();
            } else if (gesture.dx >= SWIPE_DISTANCE) {
                latest.current.onPrevious();
            }
        },
    }).panHandlers, []);
};

/** @returns {string} Translation key of the time-of-day greeting. */
const greetingKey = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 11) {
        return 'mgmt_dash_greeting_morning';
    }

    return hour >= 11 && hour < 18 ? 'mgmt_dash_greeting_day' : 'mgmt_dash_greeting_evening';
};

/**
 * @returns {JSX.Element} The workspace dashboard.
 */
export default function WorkspaceDashboardScreen() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const labels = useWorkspaceLabels();
    const ws = useWorkspaces();
    const {user: currentUser, firstName} = useCurrentUser();
    const {visible: teamChatVisible} = useChatHubBadge();
    const {scope, refresh: refreshManagement} = useManagementData();
    const {canAccess, context: accessContext} = useManagementAccess();
    const restaurantId = scope?.restaurantId ? String(scope.restaurantId) : null;
    const {modules: accessMap} = useAccessibleModules(restaurantId);

    const board = ws.activeBoard;
    const widgetBoard = isWidgetBoard(board);
    const data = useWorkspaceData(widgetBoard ? board : null, {restaurantId, enabled: widgetBoard});

    const [editing, setEditing] = useState(false);
    const [sheet, setSheet] = useState(null);
    const [settingsId, setSettingsId] = useState(null);

    const profile = useMemo(() => resolveDashboardProfile({
        placeType: scope?.restaurant?.placeType,
        roles: accessContext?.permissionsContext?.roles,
        canAccess,
    }), [scope, accessContext, canAccess]);
    const stores = useMemo(() => listUserStores(currentUser), [currentUser]);
    const allowed = useCallback((definition) => isWidgetAllowed(definition, accessMap), [accessMap]);

    const boards = useMemo(() => ws.state?.boards || [], [ws.state]);
    const index = boards.findIndex((item) => item.id === ws.state?.activeId);
    const canAdd = boards.length < MAX_WORKSPACES;

    const goTo = useCallback((offset) => {
        const target = boards[index + offset];

        if (target) {
            haptics.select();
            setEditing(false);
            ws.activate(target.id);
        }
    }, [boards, index, ws]);
    const panHandlers = useTabSwipe(() => goTo(1), () => goTo(-1), !editing && !sheet);

    const fail = useCallback((error) => {
        const message = {
            [WORKSPACE_ERRORS.MAX]: t('wsd_err_max', {max: MAX_WORKSPACES}),
            [WORKSPACE_ERRORS.MAX_WIDGETS]: t('wsd_err_widgets_max', {max: MAX_WIDGETS}),
            [WORKSPACE_ERRORS.LAST]: t('wsd_delete_last'),
            [WORKSPACE_ERRORS.EMPTY_NAME]: t('wsd_err_name'),
        }[error] || t('wsd_widget_error');

        haptics.warning();
        HappyAlert.warning(t('wsd_title'), message);
    }, [t]);

    const createBlank = useCallback((name) => {
        const result = ws.addBoard({name, icon: 'view-dashboard-edit-outline'});

        if (result?.error) {
            fail(result.error);

            return;
        }

        haptics.success();
        setSheet(null);
        setEditing(true);
    }, [ws, fail]);

    const applyTemplate = useCallback((template) => {
        const result = ws.applyTemplate(template, {name: t(templateKeys(template.id).title), isAllowed: allowed});

        if (result?.error) {
            fail(result.error);

            return;
        }

        haptics.success();
        setSheet(null);
    }, [ws, t, allowed, fail]);

    const aiWidgets = useCallback((ids) => widgetsFromIds(ids, {isAllowed: allowed}), [allowed]);

    const aiCreate = useCallback((ids) => {
        const result = ws.addBoard({name: t('wsd_ai_board_name'), icon: 'creation', widgets: aiWidgets(ids)});

        if (result?.error) {
            fail(result.error);

            return;
        }

        haptics.success();
        setSheet(null);
    }, [ws, t, aiWidgets, fail]);

    const aiReplace = useCallback((ids) => {
        if (board) {
            ws.setBoardWidgets(board.id, aiWidgets(ids));
            haptics.success();
            setSheet(null);
        }
    }, [ws, board, aiWidgets]);

    const addWidget = useCallback((type) => {
        const result = ws.addWidget(board.id, type);

        if (result?.error) {
            fail(result.error);

            return;
        }

        haptics.tap();
    }, [ws, board, fail]);

    const removeBoard = useCallback(async () => {
        const target = board;
        const confirmed = await HappyAlert.confirm({
            title: t('wsd_delete'),
            message: t('wsd_delete_confirm', {name: target?.name}),
            confirmText: t('wsd_delete'),
            destructive: true,
        });

        if (!confirmed || !target) {
            return;
        }

        const result = ws.removeBoard(target.id);

        if (result?.error) {
            fail(result.error);

            return;
        }

        haptics.heavy();
        setSheet(null);
        setEditing(false);
    }, [board, ws, t, fail]);

    const disableWorkspaces = useCallback(async () => {
        const confirmed = await HappyAlert.confirm({
            title: t('wsd_disable'),
            message: t('wsd_disable_confirm'),
            confirmText: t('wsd_disable_short'),
        });

        if (confirmed) {
            setSheet(null);
            setEditing(false);
            await ws.disable();
        }
    }, [ws, t]);

    const removeWidget = useCallback(async (widget) => {
        const confirmed = await HappyAlert.confirm({
            title: t('wsd_remove_widget'),
            message: labels.title(widget.type),
            confirmText: t('wsd_remove_widget'),
            destructive: true,
        });

        if (confirmed) {
            haptics.medium();
            ws.removeWidget(board.id, widget.id);
        }
    }, [ws, board, labels, t]);

    const onRefresh = useCallback(() => {
        refreshManagement?.();
        ws.reload();
        data.refresh();
    }, [refreshManagement, ws, data]);

    if (!ws.state || !board) {
        return <ManagementDashboardScreen />;
    }

    const strip = (
        <WorkspaceTabStrip
            boards={boards}
            activeId={board.id}
            canAdd={canAdd}
            onSelect={(id) => {
                setEditing(false);
                ws.activate(id);
            }}
            onManage={() => setSheet('manage')}
            onAdd={() => setSheet('create')}
        />
    );
    const sheets = (
        <>
            <WorkspaceManageSheet
                visible={sheet === 'manage'}
                board={board}
                canDelete={boards.length > 1}
                canDuplicate={canAdd}
                canMoveLeft={index > 0}
                canMoveRight={index < boards.length - 1}
                canDisable={board.kind === 'classic'}
                onClose={() => setSheet(null)}
                onRename={(name) => {
                    const result = ws.renameBoard(board.id, name);

                    if (result?.error) {
                        fail(result.error);
                    } else {
                        haptics.success();
                        setSheet(null);
                    }
                }}
                onDuplicate={() => {
                    const result = ws.duplicateBoard(board.id);

                    if (result?.error) {
                        fail(result.error);
                    } else {
                        haptics.success();
                        setSheet(null);
                    }
                }}
                onMove={(direction) => {
                    haptics.select();
                    ws.moveBoardBy(board.id, direction);
                }}
                onDelete={removeBoard}
                onDisable={disableWorkspaces}
            />
            <WorkspaceCreateSheet
                visible={sheet === 'create'}
                onClose={() => setSheet(null)}
                onBlank={createBlank}
                onTemplates={() => setSheet('templates')}
                onAi={() => setSheet('ai')}
            />
            <WorkspaceTemplatesSheet
                visible={sheet === 'templates'}
                onClose={() => setSheet(null)}
                profile={profile}
                accessMap={accessMap}
                canCreate={canAdd}
                onApply={applyTemplate}
            />
            <WorkspaceAiSheet
                visible={sheet === 'ai'}
                onClose={() => setSheet(null)}
                availableWidgets={WORKSPACE_WIDGETS.filter(allowed).map((definition) => ({
                    id: definition.id,
                    title: labels.title(definition.id),
                    description: definition.description,
                    category: definition.module || 'overview',
                    kind: definition.kind,
                }))}
                restaurantId={restaurantId}
                canReplace={widgetBoard}
                canCreate={canAdd}
                onCreate={aiCreate}
                onReplace={aiReplace}
            />
            <WorkspaceLibrarySheet
                visible={sheet === 'library'}
                onClose={() => setSheet(null)}
                accessMap={accessMap}
                counts={(board.widgets || []).reduce((acc, widget) => ({...acc, [widget.type]: (acc[widget.type] || 0) + 1}), {})}
                onAdd={addWidget}
            />
            <WorkspaceWidgetSettingsSheet
                visible={!!settingsId}
                onClose={() => setSettingsId(null)}
                widget={board.widgets.find((widget) => widget.id === settingsId) || null}
                stores={stores}
                onChange={(patch) => ws.setWidgetOptions(board.id, settingsId, patch)}
            />
        </>
    );

    // The first workspace: the classic Manager dashboard, tab strip injected under its header.
    if (!widgetBoard) {
        return (
            <View style={styles.fill} {...panHandlers}>
                <ManagementDashboardScreen workspaceBar={strip} />
                {sheets}
            </View>
        );
    }

    const scopeLabel = [scope?.restaurant?.name || t('mgmt_dash_hero_subtitle'), scope?.placeLabel]
        .filter((part, i, list) => !!part && list.indexOf(part) === i)
        .join(' · ');
    const eyebrow = firstName
        ? t('mgmt_dash_greeting_named', {greeting: t(greetingKey()), name: firstName, defaultValue: '{{greeting}}, {{name}}'})
        : t(greetingKey());

    return (
        <View style={styles.fill} {...panHandlers}>
            <ManagementScreen safeTop refreshing={data.refreshing} onRefresh={onRefresh}>
                <GreetingHeader
                    user={currentUser}
                    onAvatarPress={() => navigation.navigate('ManagementSettings')}
                    eyebrow={eyebrow}
                    title={t(MANAGEMENT_HERO_TITLE_KEY)}
                    subtitle={scopeLabel}
                    right={(
                        <View style={styles.headerActions}>
                            <TouchableOpacity
                                onPress={() => {
                                    haptics.tap();
                                    setEditing((value) => !value);
                                }}
                                activeOpacity={0.8}
                                accessibilityRole="button"
                                accessibilityLabel={editing ? t('wsd_done') : t('wsd_edit')}
                                style={[styles.circle, editing
                                    ? {backgroundColor: m.colors.primary, borderColor: m.colors.primary}
                                    : {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}
                            >
                                <Icon name={editing ? 'check' : 'pencil-outline'} size={20} color={editing ? '#FFFFFF' : m.colors.heading} />
                            </TouchableOpacity>
                            {teamChatVisible && (
                                <View style={[styles.circle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                                    <ChatHeaderButton size={21} color={m.colors.heading} />
                                </View>
                            )}
                            <View style={[styles.circle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                                <NotificationBellButton size={21} color={m.colors.heading} />
                            </View>
                        </View>
                    )}
                />

                {strip}

                {ws.offline && (
                    <Text style={[styles.offline, {color: m.colors.subText}]}>{t('wsd_offline')}</Text>
                )}

                <View style={styles.periodRow}>
                    <PillTabs
                        scroll
                        options={WORKSPACE_PERIODS.map((period) => ({value: period, label: t(`wsd_period_${period}`)}))}
                        value={board.period}
                        onChange={(period) => ws.setBoardPeriod(board.id, period)}
                    />
                </View>

                {editing && (
                    <View style={styles.editBar}>
                        <PrimaryButton label={t('wsd_add_widget')} icon="plus" onPress={() => setSheet('library')} />
                        <PrimaryButton label={t('wsd_templates_short')} icon="view-grid-plus-outline" variant="outline" onPress={() => setSheet('templates')} />
                        <PrimaryButton label={t('wsd_ai_short')} icon="creation" variant="outline" onPress={() => setSheet('ai')} />
                    </View>
                )}

                {board.widgets.length === 0 ? (
                    <EmptyState
                        icon="view-dashboard-edit-outline"
                        title={t('wsd_empty_title')}
                        text={t('wsd_empty_text')}
                        action={(
                            <View style={styles.editBar}>
                                <PrimaryButton label={t('wsd_add_widget')} icon="plus" onPress={() => setSheet('library')} />
                                <PrimaryButton label={t('wsd_templates_short')} icon="view-grid-plus-outline" variant="outline" onPress={() => setSheet('templates')} />
                            </View>
                        )}
                    />
                ) : (
                    <View style={styles.widgets}>
                        {board.widgets.map((widget, position) => {
                            const key = requestKey({
                                period: widget.options?.period || board.period,
                                restaurantId: widget.options?.restaurantId || restaurantId || null,
                            });
                            const body = (
                                <WorkspaceWidget
                                    widget={widget}
                                    summary={data.summaries[key]}
                                    error={data.errors[key]}
                                    onRetry={data.refresh}
                                />
                            );
                            const width = widget.size === 'S' ? '48.5%' : '100%';

                            return (
                                <View key={widget.id} style={{width}}>
                                    {editing ? (
                                        <WorkspaceEditFrame
                                            size={widget.size}
                                            isFirst={position === 0}
                                            isLast={position === board.widgets.length - 1}
                                            onMove={(direction) => ws.moveWidgetBy(board.id, widget.id, direction)}
                                            onResize={(size) => ws.resizeWidget(board.id, widget.id, size)}
                                            onSettings={() => setSettingsId(widget.id)}
                                            onRemove={() => removeWidget(widget)}
                                        >
                                            {body}
                                        </WorkspaceEditFrame>
                                    ) : body}
                                </View>
                            );
                        })}
                    </View>
                )}
            </ManagementScreen>
            {sheets}
        </View>
    );
}

const styles = StyleSheet.create({
    fill: {flex: 1},
    headerActions: {flexDirection: 'row', alignItems: 'center', gap: 8},
    circle: {width: 42, height: 42, borderRadius: 21, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    periodRow: {marginBottom: 14, marginLeft: -6},
    editBar: {flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 14},
    widgets: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between'},
    offline: {fontSize: 12, marginBottom: 8, textAlign: 'center'},
});
